/**
 * @file Shared "Sync everything" button. Used in Settings, Devices index,
 * Data quality, People — everywhere admins might want a fresh pull.
 *
 * Fully registry-driven: the stage list, run order, progress labels, toast
 * fragments and per-source audit diffs all come from lib/sync-stages.js —
 * adding a source there adds it here with no changes to this file.
 */

import { el, toast } from './utils.js';
import { Icons }     from './icons.js';
import { SYNC_STAGES, syncEverything, buildSyncAuditDetails, isSyncBusy, onSyncBusyChange, SyncBusyError } from './lib/sync-stages.js';
import { logAuditEvent }  from './audit-log.js';
import { AUDIT_ACTION }   from './lib/audit-actions.js';
import { isTestMode }     from './test-mode.js';

/**
 * Create a button element wired to syncEverything().
 * @param size 'lg' for the canonical Settings button, 'sm' for in-page
 *             toolbar use (Devices, Data quality, etc.).
 */
export function renderSyncButton({ size = 'sm' } = {}) {
  const cls = size === 'lg' ? 'btn' : 'btn-outline btn btn-sm';
  const button = el('button', { class: cls });
  const defaultChildren = [Icons.refresh(), document.createTextNode('Sync everything')];
  button.replaceChildren(...defaultChildren);
  let busy = false;

  // In test mode a real sync is meaningless and dangerous: it would hit
  // the live external APIs and (via materialise) rebuild the production
  // /devices, never touching the sandbox. Disable it.
  if (isTestMode()) {
    button.disabled = true;
    button.title = 'Disabled in test mode — syncs run against live systems, not the sandbox.';
    return button;
  }

  const progressByKey = new Map(SYNC_STAGES.map((s) => [s.key, s.progress]));

  // Grey out while ANY sync runs anywhere in this tab (a single-source
  // button, the tariffs fan-out, another instance of this button on a
  // different page render). Self-unsubscribes once detached.
  const unsubBusy = onSyncBusyChange((anyBusy) => {
    if (!button.isConnected) { unsubBusy(); return; }
    if (busy) return; // our own run manages the label + disabled state
    button.disabled = anyBusy;
    button.title = anyBusy ? 'Another sync is already running.' : '';
  });

  button.addEventListener('click', async () => {
    if (busy || isSyncBusy()) return;
    busy = true;
    button.disabled = true;
    const setLabel = (text) => button.replaceChildren(document.createTextNode(text));
    try {
      const result = await syncEverything({
        onProgress: (key, status) => {
          if (status === 'start' && progressByKey.has(key)) setLabel(progressByKey.get(key));
        },
      });
      if (result.errors.length) {
        toast(`Sync finished with ${result.errors.length} error(s) — check console.`, 'error');
        console.error('[sync] errors', result.errors);
      } else {
        const fragments = SYNC_STAGES
          .map((s) => (result[s.key] ? s.toastFragment(result[s.key]) : null))
          .filter(Boolean);
        toast(`Synced ${fragments.join(', ')}.`, 'success');
      }
      // Mirror the nightly cron's audit row for ad-hoc syncs. Same shape,
      // same renderer in the audit log page — the only difference is the
      // actor (admin who clicked the button vs. the cron service identity).
      logAuditEvent({
        action: AUDIT_ACTION.SYSTEM_SYNC_RAN,
        details: { source: 'manual', ...buildSyncAuditDetails(result) },
      });
    } catch (err) {
      toast(err instanceof SyncBusyError ? err.message : (err.message || 'Sync failed.'),
        err instanceof SyncBusyError ? 'info' : 'error');
    } finally {
      busy = false;
      button.disabled = false;
      button.replaceChildren(...defaultChildren.map((n) => n.cloneNode(true)));
    }
  });

  return button;
}
